import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { getDetails, getGeocode, getLatLng } from "use-places-autocomplete";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { useTheme } from '@mui/material/styles';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE;
const libraries = ["places"];
const autocompleteService = { current: null };

const darkMapStyle = [
    { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
    {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
    },
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#38414e" }],
    },
    {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#212a37" }],
    },
    {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#9ca5b3" }],
    },
    {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#746855" }],
    },
    {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1f2835" }],
    },
    {
        featureType: "road.highway",
        elementType: "labels.text.fill",
        stylers: [{ color: "#f3d19c" }],
    },
    {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#2f3948" }],
    },
    {
        featureType: "transit.station",
        elementType: "labels.text.fill",
        stylers: [{ color: "#d59563" }],
    },
    {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#17263c" }],
    },
    {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#515c6d" }],
    },
    {
        featureType: "water",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#17263c" }],
    },
];

const lightMapStyle = [
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
    },
    {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ visibility: "off" }]
    }
];

const CustomAddressPicker = ({ errors, setErrors, data, setData, name, validators, nullable = false, onlyHungary = true, onAddressChange, ...rest }) => {
    const theme = useTheme();
    const [value, setValue] = React.useState(() => {
        if (data?.[name]?.address_com) {
            return {
                description: data[name].address_com,
                formatted_address: data[name].address_com
            };
        }
        return rest?.value || null;
    });
    const [inputValue, setInputValue] = React.useState(data?.[name]?.address_com || '');
    const [options, setOptions] = React.useState([]);
    const [center, setCenter] = React.useState(() => {
        if (data?.[name]?.coordinates) {
            return data[name].coordinates;
        }
        return { lat: 47.4979, lng: 19.0402 }; // Budapest coordinates
    });
    const mapRef = React.useRef(null);
    const isMovingRef = React.useRef(false);
    const isMapLoadedRef = React.useRef(false);
    const mapCenterRef = React.useRef(center);
    const lastUpdateRef = React.useRef(Date.now());
    
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: libraries,
    });

    const fetch = React.useMemo(
        () =>
            debounce((request, callback) => {
                if (autocompleteService.current) {
                    autocompleteService.current.getPlacePredictions(request, callback);
                }
            }, 400),
        [],
    );

    // Debounced function to update address from coordinates
    const debouncedUpdateAddress = React.useMemo(
        () =>
            debounce(async (lat, lng) => {
                try {
                    const results = await getGeocode({ 
                        location: { lat, lng },
                        language: 'hu'
                    });
                    if (results[0]) {
                        const { address_components } = await getDetails({ placeId: results[0].place_id, fields: ["address_components"] });
                        const formattedAddress = getFormattedAddress(address_components);
                        const newAddress = {
                            address: formattedAddress,
                            coordinates: { lat, lng },
                            address_com: results[0].formatted_address
                        };
                        setData({ 
                            ...data, 
                            [name]: newAddress
                        });
                        setValue(results[0]);
                        validateAddress(newAddress);
                        if (onAddressChange) {
                            onAddressChange(newAddress);
                        }
                    }
                } catch (error) {
                    console.error('Error getting address from coordinates:', error);
                }
            }, 1000),
        [setData, name, data, onAddressChange]
    );

    const validateAddress = (address) => {
        if (!validators || !validators[name]) return;
        
        for (let validator of validators[name]) {
            const { valid, error } = validator(address);
            if (!valid) {
                setErrors({ ...errors, [name]: error });
                break;
            } else {
                setErrors({ ...errors, [name]: '' });
            }
        }
    };

    React.useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window.google) {
            autocompleteService.current =
                new window.google.maps.places.AutocompleteService();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }
        let restrictions = {}
        if (onlyHungary) {
            restrictions = { componentRestrictions: { country: 'hu' } }
        }

        fetch({ 
            input: inputValue, 
            ...restrictions,
            language: 'hu' 
        }, (results) => {
            if (active) {
                let newOptions = [];

                if (value) {
                    newOptions = [value];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    const getFormattedAddress = addressDetails => {
        const streetNumber = addressDetails?.find(d => d.types?.includes('street_number'))?.short_name || ''
        const streetName = addressDetails?.find(d => d.types?.includes('route'))?.short_name || ''
        const city = addressDetails?.find(d => d.types?.includes('locality'))?.short_name || ''
        const postalCode = addressDetails?.find(d => d.types?.includes('postal_code'))?.short_name || ''
        const country = addressDetails?.find(d => d.types?.includes('country'))?.short_name || ''
        return {
            street_number: streetNumber,
            street: streetName,
            postal_code: postalCode,
            city: city,
            country: country
        }
    }

    const onSelect = async (address) => {
        if (!address) return;
        
        try {
            const results = await getGeocode({ 
                address,
                language: 'hu'
            });
            if (!results || !results[0]) return;

            const { lat, lng } = await getLatLng(results[0]);
            
            // Update the map center
            if (isMapLoadedRef.current) {
                mapRef.current.panTo({ lat, lng });
                mapCenterRef.current = { lat, lng };
                setCenter({ lat, lng });
            }
            
            // Update the address
            const { address_components } = await getDetails({ 
                placeId: results[0].place_id, 
                fields: ["address_components"] 
            });

            const newAddress = {
                address: getFormattedAddress(address_components),
                coordinates: { lat, lng },
                address_com: address
            };

            setData({ 
                ...data, 
                [name]: newAddress
            });
            setValue(results[0]);
            validateAddress(newAddress);
            if (onAddressChange) {
                onAddressChange(newAddress);
            }
        } catch (error) {
            console.error('Error selecting address:', error);
        }
    }

    const onMapCenterChanged = () => {
        if (mapRef.current && isMapLoadedRef.current) {
            const newCenter = mapRef.current.getCenter();
            if (newCenter) {
                const lat = newCenter.lat();
                const lng = newCenter.lng();
                
                // Only update the center ref during dragging
                mapCenterRef.current = { lat, lng };
            }
        }
    };

    const onMapDragEnd = () => {
        isMovingRef.current = false;
        if (mapCenterRef.current) {
            setCenter(mapCenterRef.current);
            debouncedUpdateAddress(mapCenterRef.current.lat, mapCenterRef.current.lng);
        }
    };

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                id="interactive-google-map-demo"
                getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    if (option.description) return option.description;
                    if (option.formatted_address) return option.formatted_address;
                    return '';
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    if (typeof option === 'string' && typeof value === 'string') {
                        return option === value;
                    }
                    if (option && value) {
                        return option.description === value.description || 
                               option.formatted_address === value.formatted_address ||
                               option.place_id === value.place_id;
                    }
                    return false;
                }}
                sx={{ width: '100%', mb: 2 }}
                filterOptions={(x) => x}
                options={options}
                autoComplete
                includeInputInList
                filterSelectedOptions
                value={value}
                {...rest}
                noOptionsText="No locations"
                onChange={(event, newValue) => {
                    setOptions(newValue ? [newValue, ...options] : options);
                    setValue(newValue);
                    onSelect(newValue?.description)
                }}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        fullWidth 
                        label={rest?.label}
                        error={Boolean(errors[name])}
                        helperText={errors[name]}
                    />
                )}
                renderOption={(props, option) => {
                    // Handle string options
                    if (typeof option === 'string') {
                        return (
                            <li {...props}>
                                <Grid container alignItems="center">
                                    <Grid item sx={{ display: 'flex', width: 44 }}>
                                        <LocationOnIcon sx={{ color: 'text.secondary' }} />
                                    </Grid>
                                    <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                        <Typography>{option}</Typography>
                                    </Grid>
                                </Grid>
                            </li>
                        );
                    }

                    // Handle Google Places API options
                    const matches = option.structured_formatting?.main_text_matched_substrings || [];
                    const mainText = option.structured_formatting?.main_text || option.description || '';
                    const secondaryText = option.structured_formatting?.secondary_text || '';

                    const parts = parse(
                        mainText,
                        matches.map((match) => [match.offset, match.offset + match.length]),
                    );

                    return (
                        <li {...props}>
                            <Grid container alignItems="center">
                                <Grid item sx={{ display: 'flex', width: 44 }}>
                                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                                </Grid>
                                <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                                    {parts.map((part, index) => (
                                        <Box
                                            key={index}
                                            component="span"
                                            sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                        >
                                            {part.text}
                                        </Box>
                                    ))}
                                    {secondaryText && (
                                        <Typography variant="body2" color="text.secondary">
                                            {secondaryText}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </li>
                    );
                }}
            />
            <Box sx={{ 
                height: '400px', 
                width: '100%', 
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden'
            }}>
                <GoogleMap
                    zoom={15}
                    center={center}
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    onLoad={(map) => {
                        mapRef.current = map;
                        isMapLoadedRef.current = true;
                    }}
                    onDragStart={() => { isMovingRef.current = true; }}
                    onDragEnd={onMapDragEnd}
                    onCenterChanged={onMapCenterChanged}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        styles: theme.palette.mode === 'dark' ? darkMapStyle : lightMapStyle
                    }}
                >
                    <Marker position={center} />
                </GoogleMap>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '20px',
                        height: '20px',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            width: '2px',
                            height: '100%',
                            backgroundColor: '#ff0000',
                            transform: 'translateX(-50%)'
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            width: '100%',
                            height: '2px',
                            backgroundColor: '#ff0000',
                            transform: 'translateY(-50%)'
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default CustomAddressPicker; 